/**
 * useEntityBreadcrumb — Resolves the parent chain for breadcrumb navigation
 *
 * For a Task: Task → Instance → Workflow
 * For an Approval: Approval → Task → Workflow
 * For a Checkpoint: Checkpoint → Instance → Workflow
 *
 * Uses entity fields: parentId, workflowInstanceId, workflowId
 * to build a clickable breadcrumb trail.
 */

import { useMemo } from 'react';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import type { PrimitiveType } from '@/config/primitive-metadata';
import type { BreadcrumbSegment } from './types';

/** Primitive type labels for breadcrumbs */
const TYPE_LABELS: Partial<Record<PrimitiveType, string>> = {
  workflow: 'Workflow',
  instance: 'Instance',
  task: 'Task',
  approval: 'Approval',
  checkpoint: 'Checkpoint',
  document: 'Document',
  event: 'Event',
  notification: 'Notification',
  trace: 'Trace',
  queue: 'Queue',
};

interface UseEntityBreadcrumbOptions {
  entity: PrimitiveItem | null;
  primitiveType: PrimitiveType;
  /** Callback when a breadcrumb segment is clicked */
  onNavigate?: (primitiveType: PrimitiveType, entityId: string) => void;
}

export function useEntityBreadcrumb({
  entity,
  primitiveType,
  onNavigate,
}: UseEntityBreadcrumbOptions): BreadcrumbSegment[] {
  return useMemo(() => {
    if (!entity) return [];

    const segments: BreadcrumbSegment[] = [];

    // Walk up the parent chain from entity fields
    const workflowId = entity.workflowId as string | undefined;
    const workflowInstanceId = entity.workflowInstanceId as string | undefined;
    const parentId = entity.parentId as string | undefined;

    // DRR-124: Breadcrumbs use entity names, never raw internal IDs.
    // Internal IDs like "17-inst-004" leak tenant_num.

    // Workflow level (top of chain)
    if (workflowId) {
      const workflowName = (entity.workflowName as string) ?? 'Workflow';
      segments.push({
        label: workflowName,
        primitiveType: 'workflow',
        entityId: workflowId,
        onClick: onNavigate ? () => onNavigate('workflow', workflowId) : undefined,
      });
    }

    // Instance level
    if (workflowInstanceId && workflowInstanceId !== workflowId) {
      const instanceName = (entity.instanceName as string) ?? 'Instance';
      segments.push({
        label: instanceName,
        primitiveType: 'instance',
        entityId: workflowInstanceId,
        onClick: onNavigate ? () => onNavigate('instance', workflowInstanceId) : undefined,
      });
    }

    // Parent level (for nested tasks, sub-approvals, etc.)
    if (parentId && parentId !== workflowInstanceId && parentId !== workflowId) {
      segments.push({
        label: 'Parent',
        entityId: parentId,
        onClick: onNavigate ? () => onNavigate(primitiveType, parentId) : undefined,
      });
    }

    // Current entity (always last, not clickable)
    const typeLabel = TYPE_LABELS[primitiveType] ?? primitiveType;
    const entityName = (entity.name as string) ?? typeLabel;
    segments.push({
      label: `${typeLabel}: ${entityName}`,
      primitiveType,
      entityId: entity.id as string,
    });

    return segments;
  }, [entity, primitiveType, onNavigate]);
}
